/* Form for adding a single URL item (article, video, etc.) to queue, backlog, or feed.
   For YouTube URLs, uses getVideoInfo for proper title/thumbnail/channel extraction.
   For other URLs, uses getWebpageMetadata for generic scraping. */

import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useMutation, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Link as LinkIcon } from "lucide-react";
import { posthog } from "@/lib/posthog";
import { Events } from "@/lib/posthogEvents";

type Destination = "queued" | "backlog" | "in_feed";

const DESTINATION_LABELS: Record<Destination, string> = {
  queued: "Queue",
  backlog: "Backlog",
  in_feed: "Feed",
};

function isValidUrl(str: string): boolean {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
}

function isYouTubeUrl(url: string): boolean {
  return /youtube\.com|youtu\.be/i.test(url);
}

interface AddSingleItemFormProps {
  onSuccess: () => void;
  footerContainer?: HTMLElement | null;
}

export function AddSingleItemForm({ onSuccess, footerContainer }: AddSingleItemFormProps) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [thumbnail, setThumbnail] = useState<string | undefined>();
  const [duration, setDuration] = useState<number | undefined>();
  const [destination, setDestination] = useState<Destination>("queued");
  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track whether user has manually edited title/author so we don't overwrite
  const titleEditedRef = useRef(false);
  const authorEditedRef = useRef(false);
  // Track which URL fetch is in-flight to avoid stale results
  const fetchUrlRef = useRef("");

  const addItem = useMutation(api.feedItems.addUrlToDestination);
  const getMetadata = useAction(api.feedItems.getWebpageMetadata);
  const getVideoInfo = useAction(api.videos.getVideoInfo);
  const getMetadataRef = useRef(getMetadata);
  getMetadataRef.current = getMetadata;
  const getVideoInfoRef = useRef(getVideoInfo);
  getVideoInfoRef.current = getVideoInfo;

  // Auto-fetch metadata when URL changes
  useEffect(() => {
    const trimmed = url.trim();
    if (!trimmed || !isValidUrl(trimmed)) {
      fetchUrlRef.current = "";
      setIsFetching(false);
      return;
    }

    const timer = setTimeout(async () => {
      fetchUrlRef.current = trimmed;
      setIsFetching(true);
      try {
        if (isYouTubeUrl(trimmed)) {
          const info = await getVideoInfoRef.current({ url: trimmed });
          if (fetchUrlRef.current !== trimmed) return;
          if (info) {
            if (!titleEditedRef.current) {
              setTitle(info.title?.trim() || "Untitled Video");
            }
            if (!authorEditedRef.current && info.channelName) setAuthor(info.channelName);
            setThumbnail(info.thumbnail);
            setDuration(info.duration);
          } else {
            const metadata = await getMetadataRef.current({ url: trimmed });
            if (fetchUrlRef.current !== trimmed) return;
            if (metadata && !titleEditedRef.current) {
              setTitle(metadata.title?.trim() || "Untitled Video");
            }
            setDuration(undefined);
          }
        } else {
          const metadata = await getMetadataRef.current({ url: trimmed });
          if (fetchUrlRef.current !== trimmed) return;
          if (metadata) {
            if (!titleEditedRef.current) {
              setTitle(metadata.title?.trim() || "Untitled Item");
            }
            if (metadata.thumbnail) setThumbnail(metadata.thumbnail);
          }
          setDuration(undefined);
        }
      } catch {
        // Silently fail — user can still enter title manually
      } finally {
        if (fetchUrlRef.current === trimmed) {
          setIsFetching(false);
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [url]);

  function handleUrlChange(value: string) {
    setUrl(value);
    setError(null);
    fetchUrlRef.current = value.trim();
    // Reset auto-fetched fields and manual-edit flags
    titleEditedRef.current = false;
    authorEditedRef.current = false;
    setTitle("");
    setAuthor("");
    setThumbnail(undefined);
    setDuration(undefined);
  }

  const handleSubmit = useCallback(async () => {
    const trimmedUrl = url.trim();
    const trimmedTitle = title.trim();
    if (!trimmedUrl || !trimmedTitle) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await addItem({
        url: trimmedUrl,
        title: trimmedTitle,
        thumbnail,
        destination,
        directChannelName: author.trim() || undefined,
        duration,
      });
      posthog.capture(Events.SINGLE_ITEM_ADDED, { destination, contentType: isYouTubeUrl(trimmedUrl) ? "youtube" : "article" });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add item");
    } finally {
      setIsSubmitting(false);
    }
  }, [url, title, thumbnail, destination, author, duration, addItem, onSuccess]);

  const canSubmit = url.trim().length > 0 && title.trim().length > 0 && !isSubmitting;

  return (
    <div className="space-y-4 pt-2">
      <div className="space-y-2">
        <Label htmlFor="single-item-url">URL</Label>
        <div className="relative">
          <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            id="single-item-url"
            className="pl-9"
            placeholder="https://example.com/article or YouTube URL"
            value={url}
            onChange={(e) => handleUrlChange(e.target.value)}
            autoComplete="off"
            autoFocus
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="single-item-title">Title</Label>
        <div className="flex items-center gap-2">
          <Input
            id="single-item-title"
            placeholder={isFetching ? "Fetching title..." : "Enter title"}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              titleEditedRef.current = true;
            }}
            disabled={!url.trim()}
          />
          {isFetching && (
            <Loader2 className="size-4 animate-spin text-muted-foreground shrink-0" />
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="single-item-author">Author / Channel</Label>
        <Input
          id="single-item-author"
          placeholder="Channel name or author"
          value={author}
          onChange={(e) => {
            setAuthor(e.target.value);
            authorEditedRef.current = true;
          }}
          disabled={!url.trim()}
        />
      </div>

      {thumbnail && (
        <img
          src={thumbnail}
          alt="Thumbnail preview"
          className="w-full max-h-32 object-cover rounded-md bg-muted"
        />
      )}

      <div className="space-y-2">
        <Label>Add to</Label>
        <div className="flex gap-2">
          {(Object.entries(DESTINATION_LABELS) as [Destination, string][]).map(
            ([value, label]) => (
              <Button
                key={value}
                type="button"
                variant={destination === value ? "default" : "outline"}
                size="sm"
                onClick={() => setDestination(value)}
              >
                {label}
              </Button>
            ),
          )}
        </div>
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}

      {footerContainer ? createPortal(
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          Add to {DESTINATION_LABELS[destination]}
        </Button>,
        footerContainer,
      ) : (
        <Button className="w-full" onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting && <Loader2 className="size-4 animate-spin" />}
          Add to {DESTINATION_LABELS[destination]}
        </Button>
      )}
    </div>
  );
}
